import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { fetchEventShare } from '../lib/shareApi'
import { cdnUrl } from '../lib/cdnUrl'
import { SP, openInApp, deeplinkFor, attemptOpenApp } from '../lib/shareTokens'
import { usePageMeta } from '../lib/usePageMeta'
import { resolveLang, STRINGS, intlLocale, fallbackDescription, type Lang } from '../lib/i18n'
import { applyStateOf } from '../lib/registrationMode'
import type { EventShare } from '../types/share'
import SmartBanner from '../components/share/SmartBanner'
import Hero from '../components/share/Hero'
import Wordmark from '../components/share/Wordmark'
import StationMark from '../components/share/StationMark'
import StickyCTA from '../components/share/StickyCTA'
import RsvpSheet from '../components/share/RsvpSheet'
import { useSession } from '../lib/session'
import { buildRsvpReturnUrl, hasRsvpConfirm, stripRsvpConfirm, trackingParams } from '../lib/rsvpLink'
import {
  isFreshAccount,
  resolveAuthReturn,
  takeEmailConfirmed,
  takeInitialAuthReturn,
  takePendingAuth,
} from '../lib/authReturn'
import { buildEventProperties, track, trackOnce } from '../lib/analytics'
import type { RsvpOutcome } from '../lib/rsvpApi'

// ON-407: outcomes after which a refresh must not re-open the confirm prompt.
const SETTLED: RsvpOutcome['kind'][] = ['registered', 'waitlisted', 'already_registered', 'already_waitlisted']

interface SheetState {
  step: 'signup' | 'login' | 'confirm'
  notice: string | null
  /** Opened as "email confirmed — log in": a log-in completes a new account. */
  afterEmailConfirm?: boolean
}

function formatPace(pace: number | null): string {
  if (!pace) return '—'
  const min = Math.floor(pace)
  const sec = Math.round((pace - min) * 60)
  return `${min}:${String(sec).padStart(2, '0')}`
}

function formatDate(iso: string, lang: Lang) {
  const d = new Date(iso)
  const locale = intlLocale[lang]
  const day = String(d.getDate()).padStart(2, '0')
  // Uppercasing is a no-op for zh-TW ("7月"), which is what we want — the badge
  // is styled for a short token in either language.
  const month = d.toLocaleString(locale, { month: 'short' }).toUpperCase()
  const year = d.getFullYear()
  const weekday = d.toLocaleString(locale, { weekday: 'short' })
  const time = d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: true })
  // Derive a short timezone label (e.g. "HKT", "GMT+8") from the viewer's locale
  const tzParts = d.toLocaleTimeString(locale, { timeZoneName: 'short' }).split(' ')
  const tzLabel = tzParts[tzParts.length - 1] ?? ''
  // Composed rather than interpolated at the call site: "JUL 12, 2026" and
  // "2026年7月12日" do not share an ordering, so the template has to differ.
  const dateLine = d.toLocaleDateString(locale, {
    year: 'numeric',
    month: lang === 'en' ? 'short' : 'long',
    day: 'numeric',
  })
  return { day, month, year, weekday, time, tzLabel, dateLine }
}

// ON-464: "Apply by Sat, Jul 12, 11:59 PM GMT+8" / "7月12日 週六 下午11:59 GMT+8 前申請".
// Shown in the viewer's local time like the event time above it, so it carries
// the same kind of timezone label. The label is appended rather than requested
// through timeZoneName, which zh-TW renders in brackets mid-string.
function formatDeadline(iso: string, lang: Lang): string {
  const d = new Date(iso)
  const locale = intlLocale[lang]
  const when = d.toLocaleString(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
  const tz = new Intl.DateTimeFormat(locale, { timeZoneName: 'short' })
    .formatToParts(d)
    .find((part) => part.type === 'timeZoneName')?.value
  return tz ? `${when} ${tz}` : when
}

// setTimeout overflows past 2^31-1 ms (~24.8 days) and fires immediately.
const MAX_TIMEOUT_MS = 2_147_483_647

export default function EventSharePage() {
  const { id } = useParams<{ id: string }>()
  const [event, setEvent] = useState<EventShare | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  // ON-464: the clock applyStateOf judges the deadline against. Bumped when the
  // deadline passes so a page left open flips to "closed" without a reload.
  const [now, setNow] = useState(() => new Date())

  // ON-333: ?lang= is set by the app when it builds the share link, so the page
  // matches the language of the preview the recipient tapped. The Pages Function
  // resolves the same param for the meta tags and the <html lang> attribute.
  //
  // Read through useLocation rather than window.location: a history navigation
  // that changes only the query string leaves this route mounted, and reading
  // the global directly would keep serving the language the page first loaded
  // with.
  const { search, pathname } = useLocation()
  const navigate = useNavigate()
  const lang = useMemo(() => resolveLang(search, navigator.languages), [search])
  const s = STRINGS[lang]

  // ON-407: web RSVP. One shared Supabase session (also the captain portal's).
  const { user, loading: authLoading } = useSession()
  const [sheet, setSheet] = useState<SheetState | null>(null)

  // <html lang> tracks the URL in every render state — loading and not-found
  // included, not just once an event has loaded. Owned here rather than in
  // usePageMeta because that hook has nothing to describe until then, which
  // would leave a Chinese error page declaring itself English.
  useEffect(() => {
    const original = document.documentElement.lang
    document.documentElement.lang = lang
    return () => {
      document.documentElement.lang = original
    }
  }, [lang])

  useEffect(() => {
    if (!id) return
    fetchEventShare(id)
      .then(setEvent)
      .catch(() => setFailed(true))
      .finally(() => setLoading(false))
  }, [id])

  const deadline = event?.registration_deadline
  useEffect(() => {
    const at = deadline ? Date.parse(deadline) : NaN
    if (Number.isNaN(at)) return
    const current = Date.now()
    const wait = at - current
    if (wait <= 0) {
      // The preview arrived (or we navigated to it) after its deadline while
      // `now` still predates it: catch the clock up so the page shows closed.
      // Guarded so it settles after one update instead of looping.
      if (now.getTime() < at) setNow(new Date(current))
      return
    }
    // A far-off deadline re-arms in steps: each tick updates `now`, which
    // re-runs this effect with the remaining wait.
    const timer = setTimeout(() => setNow(new Date()), Math.min(wait + 1000, MAX_TIMEOUT_MS))
    return () => clearTimeout(timer)
  }, [deadline, now])

  // ON-464: null for open registration, which keeps its "N spots left" stat.
  const apply = event ? applyStateOf(event, now) : null

  const heroImageUrl = useMemo(() => {
    const coverPath = event?.cover_photo_paths?.[0]
    if (coverPath) return cdnUrl('event-photos', coverPath)
    if (event?.club_profile_image_path) return cdnUrl('group-images', event.club_profile_image_path)
    return undefined
  }, [event])

  usePageMeta(
    event
      ? {
          title: `${event.title} — ${event.club_name}`,
          // Matches the fallback the Pages Function builds, so the tags a crawler
          // scrapes and the ones a browser ends up with do not disagree.
          description:
            event.description ??
            fallbackDescription(s, event.event_type, event.club_name, apply?.mode ?? null, apply?.closed),
          imageUrl: heroImageUrl,
          iosAppId: import.meta.env.VITE_IOS_APP_STORE_ID,
        }
      : loading
        ? null
        // Once the fetch has settled with nothing to show, the title should say
        // so in the language the page is rendering, instead of leaving the
        // static shell's generic English one in place.
        : { title: s.notFoundTitle },
  )

  useEffect(() => {
    // ON-455: `search` carries the link's query string (ref/src tags,
    // open=chat, ...) onto the hand-off.
    // Not a dependency — the attempt is once per session and must not re-fire
    // on a query-only navigation.
    // ON-407: never on a return from auth (?rsvp=confirm) — bouncing to the
    // app would strand the runner mid-RSVP (and drop the web session's tokens).
    if (event?.id && !hasRsvpConfirm(search)) attemptOpenApp('event', event.id, search)
  }, [event?.id])

  // ON-407 analytics context: event_id, club_id and the link's ref / src.
  const eventProps = useCallback(
    (extra: Record<string, unknown> = {}) =>
      event ? buildEventProperties({ eventId: event.id, clubId: event.club_id, search }, extra) : extra,
    [event, search],
  )

  // auth_completed (+ registration_completed for a new account) once a
  // session exists. Held in state so it also flushes when the session arrives
  // after the signal (an email confirmed in another browser, then a password
  // log-in here). isNew null = judge by the account's age (Google / Apple).
  const [pendingAuth, setPendingAuthCompletion] = useState<{
    method: 'email' | 'google' | 'apple' | 'password'
    isNew: boolean | null
  } | null>(null)
  useEffect(() => {
    if (!pendingAuth || !user || !event) return
    setPendingAuthCompletion(null)
    const isNew = pendingAuth.isNew ?? isFreshAccount(user.created_at)
    track('auth_completed', eventProps({ account_match: isNew ? 'new' : 'existing', method: pendingAuth.method }))
    if (isNew) {
      const created = user.created_at ? Date.parse(user.created_at) : NaN
      trackOnce(
        `registration_completed:${user.id}`,
        'registration_completed',
        eventProps({
          signup_surface: 'web',
          ...(Number.isNaN(created) ? {} : { time_to_complete_ms: Math.max(0, Date.now() - created) }),
        }),
      )
    }
  }, [pendingAuth, user, event, eventProps])

  // Once per page load, when both the event and the session state are known:
  // event_page_opened, plus whatever an auth round trip left on the URL.
  const handledLoad = useRef(false)
  useEffect(() => {
    if (!event || authLoading || handledLoad.current) return
    handledLoad.current = true
    const resolved = resolveAuthReturn({
      authReturn: takeInitialAuthReturn(pathname),
      hasSession: !!user,
      rsvpConfirm: hasRsvpConfirm(search),
      pending: takePendingAuth(),
      confirmedViaAuthConfirm: takeEmailConfirmed(),
    })
    track('event_page_opened', eventProps({ is_authenticated: !!user }))
    if (resolved.linkOpenedHere) track('auth_link_opened', eventProps({ link_type: 'signup' }))
    if (resolved.method) {
      // An email confirmation is a new account by definition; an OAuth
      // return is judged by the account's age once the session is known.
      setPendingAuthCompletion({
        method: resolved.method,
        isNew: resolved.method === 'email' && resolved.emailConfirmed ? true : null,
      })
    }
    switch (resolved.step) {
      case 'confirm':
        setSheet({ step: 'confirm', notice: null })
        break
      case 'login_after_confirm':
        setSheet({ step: 'login', notice: s.rsvp.emailConfirmedLogin, afterEmailConfirm: true })
        break
      case 'login_link_expired':
        setSheet({ step: 'login', notice: s.rsvp.linkExpired })
        break
      case 'login':
        setSheet({ step: 'login', notice: s.rsvp.genericError })
        break
    }
  }, [event, authLoading, user, pathname, search, eventProps, s])

  const dropRsvpParam = useCallback(() => {
    if (hasRsvpConfirm(search)) navigate({ pathname, search: stripRsvpConfirm(search) }, { replace: true })
  }, [navigate, pathname, search])

  if (loading) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: SP.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          border: `3px solid ${SP.border}`,
          borderTopColor: SP.lime,
          animation: 'spin 0.8s linear infinite',
        }} />
      </div>
    )
  }

  if (failed || !event) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: SP.bg, color: SP.text, fontFamily: SP.ui, WebkitFontSmoothing: 'antialiased' }}>
        <SmartBanner onOpen={() => {}} />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 'calc(100% - 57px)', padding: '0 40px', textAlign: 'center' }}>
          <StationMark size={44} color={SP.dim} style={{ marginBottom: 24, opacity: 0.4 }} />
          <h2 style={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.8, margin: '0 0 12px', color: SP.text }}>{s.notFoundTitle}</h2>
          <p style={{ fontSize: 15, color: SP.muted, fontFamily: SP.body, fontStyle: 'italic', lineHeight: 1.6, margin: 0 }}>
            {failed ? s.loadFailed : s.notFoundBody}
          </p>
        </div>
        <div style={{ position: 'absolute', bottom: 32, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
          <Wordmark height={16} color={SP.dim} />
        </div>
      </div>
    )
  }

  const { day, month, year, weekday, time, tzLabel, dateLine } = formatDate(event.event_datetime, lang)
  const spotsLeft = !apply && event.max_participants != null ? Math.max(0, event.max_participants - event.current_registrations) : null
  const ctaLabel = !apply ? s.joinEventCta : apply.closed ? s.viewEventCta : s.applyCta
  const handleOpen = () => openInApp('event', event.id, stripRsvpConfirm(search))
  // ON-407: open registration RSVPs on the web; selection / lottery events
  // are applied for in the app (ON-464), so they keep the app CTA.
  const webRsvp = !apply
  const handleRsvp = () => {
    track('rsvp_attempted', eventProps({ is_authenticated: !!user }))
    setSheet({ step: user ? 'confirm' : 'signup', notice: null })
  }
  // ON-390: covers-at-creation plus published post-event gallery photos. Only
  // the number is shown — a logged-out visitor never sees the photos themselves.
  const photoCount = (event.cover_photo_paths?.length ?? 0) + (event.gallery_photo_count ?? 0)

  return (
    <div style={{ position: 'fixed', inset: 0, overflowY: 'auto', background: SP.bg, zIndex: 100 }}>
      <div style={{ minHeight: '100%', background: SP.bg, color: SP.text, fontFamily: SP.ui, WebkitFontSmoothing: 'antialiased' }}>
        <SmartBanner
          onOpen={handleOpen}
          deeplink={deeplinkFor('event', event.id, search)}
          subtitle={apply && !apply.closed ? s.applyBannerSubtitle : undefined}
        />

        <Hero imageUrl={heroImageUrl} height={420}>
          <div style={{ position: 'absolute', top: 20, left: 20 }}>
            <Wordmark height={18} />
          </div>
          <div style={{
            position: 'absolute', top: 20, right: 20,
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            padding: '10px 14px', borderRadius: 14,
            background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
            border: `1px solid ${SP.borderBright}`,
          }}>
            <div style={{ fontSize: 10, fontFamily: SP.mono, color: SP.lime, letterSpacing: 2, fontWeight: 700 }}>{month}</div>
            <div style={{ fontFamily: SP.mono, fontSize: 32, color: SP.text, fontWeight: 500, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{day}</div>
            <div style={{ fontSize: 9, fontFamily: SP.mono, color: SP.muted, letterSpacing: 1.5 }}>{year}</div>
          </div>
          <div style={{ position: 'absolute', left: 20, right: 20, bottom: 24 }}>
            {apply && (
              <div style={{ marginBottom: 12 }}>
                <span data-testid="registration-mode-badge" style={{
                  display: 'inline-block', padding: '5px 11px', borderRadius: 999,
                  background: SP.lime, color: SP.charcoal,
                  fontSize: 10, fontFamily: SP.mono, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase',
                }}>
                  {s.modeBadge[apply.mode]}
                </span>
              </div>
            )}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11, fontFamily: SP.mono, color: SP.lime, letterSpacing: 2.5, textTransform: 'uppercase', fontWeight: 600, marginBottom: 14 }}>
              <StationMark size={10} color={SP.lime} />
              {s.eventBadge} · {s.eventTypes[event.event_type] ?? event.event_type}{event.series_name ? ` · ${event.series_name}` : ''}
            </div>
            <h1 style={{ fontSize: 38, fontWeight: 800, letterSpacing: -1.5, lineHeight: 0.95, color: SP.text, margin: 0 }}>
              {event.title}
            </h1>
            {/* The club name leads in Chinese ("<club> 主辦") and trails in
                English ("Hosted by <club>"), so the label swaps sides rather
                than being interpolated into a single template. */}
            <div style={{ marginTop: 14, fontFamily: SP.body, fontStyle: 'italic', fontSize: 16, color: SP.muted, lineHeight: 1.4 }}>
              {lang === 'en' ? (
                <>
                  {s.hostedByLabel} <span style={{ color: SP.text, fontStyle: 'normal', fontWeight: 600 }}>{event.club_name}</span>
                </>
              ) : (
                <>
                  <span style={{ color: SP.text, fontStyle: 'normal', fontWeight: 600 }}>{event.club_name}</span> {s.hostedByLabel}
                </>
              )}
            </div>
          </div>
        </Hero>

        {/* When & Where */}
        <div style={{ padding: '28px 20px 8px', borderBottom: `1px solid ${SP.border}` }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div>
              <div style={{ fontSize: 11, fontFamily: SP.mono, color: SP.muted, letterSpacing: 2.5, textTransform: 'uppercase', marginBottom: 10 }}>{s.when}</div>
              <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: -0.3 }}>{weekday}</div>
              <div style={{ fontFamily: SP.mono, fontSize: 14, color: SP.text, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>
                {time}{tzLabel && <span style={{ fontSize: 11, color: SP.dim, marginLeft: 6 }}>{tzLabel}</span>}
              </div>
              <div style={{ fontSize: 11, color: SP.dim, fontFamily: SP.mono, marginTop: 4, letterSpacing: 0.5 }}>{dateLine}</div>
            </div>
            {event.start_location_name && (
              <div>
                <div style={{ fontSize: 11, fontFamily: SP.mono, color: SP.muted, letterSpacing: 2.5, textTransform: 'uppercase', marginBottom: 10 }}>{s.where}</div>
                <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: -0.3 }}>{event.start_location_name}</div>
              </div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div style={{ margin: '0 20px', padding: '18px 0', borderBottom: `1px solid ${SP.border}`, display: 'flex', gap: 12 }}>
          {event.distance_km != null && (
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: SP.mono, fontSize: 22, color: SP.text, fontWeight: 500, lineHeight: 1, fontVariantNumeric: 'tabular-nums', letterSpacing: -0.5 }}>
                {event.distance_km}<span style={{ fontSize: 11, opacity: 0.5, marginLeft: 3 }}>km</span>
              </div>
              <div style={{ fontSize: 10, fontFamily: SP.mono, color: SP.muted, letterSpacing: 1.5, marginTop: 6, textTransform: 'uppercase' }}>{s.distance}</div>
            </div>
          )}
          {event.expected_pace != null && (
            <div style={{ flex: 1, paddingLeft: 12, borderLeft: `1px solid ${SP.border}` }}>
              <div style={{ fontFamily: SP.mono, fontSize: 22, color: SP.text, fontWeight: 500, lineHeight: 1, fontVariantNumeric: 'tabular-nums', letterSpacing: -0.5 }}>
                {formatPace(event.expected_pace)}
              </div>
              <div style={{ fontSize: 10, fontFamily: SP.mono, color: SP.muted, letterSpacing: 1.5, marginTop: 6, textTransform: 'uppercase' }}>{s.pace}</div>
            </div>
          )}
          <div style={{ flex: 1, paddingLeft: 12, borderLeft: `1px solid ${SP.border}` }}>
            <div style={{ fontFamily: SP.mono, fontSize: 22, color: SP.text, fontWeight: 500, lineHeight: 1, letterSpacing: -0.5 }}>
              {s.difficulty[event.difficulty_level] ?? event.difficulty_level}
            </div>
            <div style={{ fontSize: 10, fontFamily: SP.mono, color: SP.muted, letterSpacing: 1.5, marginTop: 6, textTransform: 'uppercase' }}>{s.level}</div>
          </div>
          {spotsLeft != null && (
            <div style={{ flex: 1, paddingLeft: 12, borderLeft: `1px solid ${SP.border}` }}>
              <div style={{ fontFamily: SP.mono, fontSize: 22, color: SP.lime, fontWeight: 500, lineHeight: 1, fontVariantNumeric: 'tabular-nums', letterSpacing: -0.5 }}>
                {spotsLeft}
              </div>
              <div style={{ fontSize: 10, fontFamily: SP.mono, color: SP.muted, letterSpacing: 1.5, marginTop: 6, textTransform: 'uppercase' }}>{s.spotsLeft}</div>
            </div>
          )}
        </div>

        {/* How spots are given out (ON-464) — replaces "N spots left" for selection / lottery */}
        {apply && (
          <div data-testid="registration-mode" style={{ margin: '0 20px', padding: '18px 0', borderBottom: `1px solid ${SP.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: SP.text, letterSpacing: -0.2 }}>
                {s.spotsBy[apply.mode](apply.spots)}
              </div>
              {apply.closed ? (
                <div data-testid="registration-status" style={{ fontSize: 11, fontFamily: SP.mono, color: SP.muted, letterSpacing: 1.5, marginTop: 6, textTransform: 'uppercase' }}>
                  {s.selectionClosed}
                </div>
              ) : apply.applyBy ? (
                <div data-testid="registration-status" style={{ fontSize: 11, fontFamily: SP.mono, color: SP.lime, letterSpacing: 0.5, marginTop: 6 }}>
                  {s.applyBy(formatDeadline(apply.applyBy, lang))}
                </div>
              ) : null}
            </div>
            {apply.applied != null && apply.applied > 0 && (
              <div data-testid="registration-applied" style={{ paddingLeft: 12, borderLeft: `1px solid ${SP.border}`, flexShrink: 0 }}>
                <div style={{ fontFamily: SP.mono, fontSize: 22, color: SP.lime, fontWeight: 500, lineHeight: 1, fontVariantNumeric: 'tabular-nums', letterSpacing: -0.5 }}>
                  {apply.applied}
                </div>
                <div style={{ fontSize: 10, fontFamily: SP.mono, color: SP.muted, letterSpacing: 1.5, marginTop: 6, textTransform: 'uppercase' }}>{s.applied}</div>
              </div>
            )}
          </div>
        )}

        {/* Description */}
        {event.description && (
          <section style={{ padding: '30px 20px 8px' }}>
            <div style={{ fontSize: 11, fontFamily: SP.mono, color: SP.muted, letterSpacing: 2.5, textTransform: 'uppercase', marginBottom: 18 }}>{s.theRun}</div>
            <p style={{ fontFamily: SP.body, fontStyle: 'italic', fontSize: 19, lineHeight: 1.5, color: SP.text, margin: 0, letterSpacing: -0.1 }}>
              <span style={{
                float: 'left',
                fontFamily: SP.ui,
                fontStyle: 'normal',
                fontSize: 52,
                fontWeight: 800,
                lineHeight: 0.85,
                marginRight: 8,
                marginTop: 4,
                color: SP.lime,
                letterSpacing: -2,
              }}>
                {event.description.charAt(0)}
              </span>
              {event.description.slice(1)}
            </p>
          </section>
        )}

        {/* Photos teaser (ON-390) */}
        {photoCount > 0 && (
          <button
            data-testid="photos-teaser"
            onClick={handleOpen}
            style={{
              display: 'flex', alignItems: 'center', gap: 12,
              width: 'calc(100% - 40px)', margin: '30px 20px 0', padding: '14px 16px',
              borderRadius: 14, border: `1px solid ${SP.borderBright}`,
              background: SP.card, color: SP.text, textAlign: 'left', cursor: 'pointer',
              fontFamily: SP.ui, fontSize: 14, fontWeight: 600, lineHeight: 1.4,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
              <rect x="3" y="5" width="18" height="14" rx="2" stroke={SP.lime} strokeWidth="2" />
              <circle cx="9" cy="10" r="1.5" fill={SP.lime} />
              <path d="M21 16l-5-5-9 8" stroke={SP.lime} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span style={{ flex: 1 }}>{s.photosTeaser(photoCount)}</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
              <path d="M5 12h14m-6-6l6 6-6 6" stroke={SP.lime} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}

        {/* Footer */}
        <div style={{ padding: '40px 20px 20px', textAlign: 'center', borderTop: `1px solid ${SP.border}`, marginTop: 30 }}>
          <Wordmark height={20} />
        </div>

        <StickyCTA
          variant="event"
          onOpen={handleOpen}
          label={webRsvp ? s.rsvp.openInApp : ctaLabel}
          rsvp={webRsvp ? { label: s.rsvp.rsvpCta, onClick: handleRsvp } : undefined}
        />

        {sheet && (
          <RsvpSheet
            key={`${sheet.step}:${sheet.notice ?? ''}`}
            s={s.rsvp}
            eventId={event.id}
            eventTitle={event.title}
            returnUrl={buildRsvpReturnUrl(window.location.origin, pathname, search)}
            initialStep={sheet.step}
            initialNotice={sheet.notice}
            analyticsProps={eventProps()}
            tracking={{ ...trackingParams(search), lang }}
            onClose={() => {
              setSheet(null)
              dropRsvpParam()
            }}
            onOpenApp={handleOpen}
            onRsvpSettled={(outcome) => {
              if (SETTLED.includes(outcome.kind)) dropRsvpParam()
            }}
            onPasswordSignIn={() =>
              setPendingAuthCompletion({ method: 'password', isNew: !!sheet.afterEmailConfirm })
            }
          />
        )}
      </div>
    </div>
  )
}
